<template>
  <q-layout view="hHh Lpr lFf" class="bg-background">
    <q-header
      bordered
      height-hint="70"
      class="bg-background header-height q-px-md"
    >
      <header-menu />
    </q-header>
    <!-- SIDE MENU -->

    <side-menu
      v-if="userStore.isLoggedIn"
      :link-actions="receiverLinks"
      :check-roles="checkRoles"
      :bin="{
        to: '/bin/templates',
      }"
      :subscription="userSubscription"
    />
    <!-- CONTENT -->
    <q-page-container>
      <alert-banner
        :banner="userStore.alertBanner"
        @update:banner="updateAlertBanner"
      />
      <q-page
        class="q-py-lg container-fluid"
        :class="$route.meta.fluid ? $route.meta.fluid : 'container-md'"
      >
        <router-view v-slot="{ Component }">
          <transition
            mode="out-in"
            enter-active-class="animated fadeIn"
            leave-active-class="animated fadeOut"
          >
            <component :is="Component" />
          </transition>
        </router-view>
      </q-page>
    </q-page-container>
  </q-layout>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useUserStore } from 'stores/receiver/account/user/user'

import { useImpersonate } from 'composables/receiver/user/useImpersonate'
import { useCheckRoles } from 'composables/receiver/user/useCheckRoles'

import HeaderMenu from 'components/receiver/layout/HeaderMenu.vue'
import SideMenu from 'components/common/layout/SideMenu.vue'
import AlertBanner from 'components/common/layout/AlertBanner.vue'

import type { AlertBannerType } from 'types/common/account/user'

const userStore = useUserStore()

const { impersonateLink } = useImpersonate()
const { checkRoles } = useCheckRoles()

const userSubscription = computed(() => {
  if (!userStore.subscription) return null
  return {
    ...userStore.subscription,
    link: {
      to: '/account/billing/subscription',
    },
    planLink: { to: '/pricing/plans' },
  }
})

const isOnboarding = computed(
  () => userStore.activeOrganisation?.number_of_self_transformations === 0
)

const receiverLinks = computed(() => ({
  letsStart: {
    to: { name: 'lets-start-page' },
    display: () => isOnboarding.value,
    icon: 'fa-solid fa-rocket',
  },
  overview: {
    to: { name: 'overview' },
    display: () =>
      userStore.activeOrganisation
        ?.number_of_received_submissions_from_others !== 0,
  },
  templates: {
    to: { name: 'manage-templates' },
  },
  customers: {
    to: { name: 'manage-customers' },
    display: () => userStore.activeOrganisation?.number_of_customers !== 0,
  },
  senderWorkflows: {
    action: () => {
      void impersonateLink({ url: '/app/workflows' })
    },
  },
  integrations: {
    to: { name: 'manage-integrations' },
  },
}))

const updateAlertBanner = (banner: AlertBannerType | null) => {
  if (!userStore.user) return
  userStore.user.banner = banner
}
</script>
